import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, zip } from 'rxjs';
import { distinctUntilChanged, map, take, withLatestFrom } from 'rxjs/operators';

import { Translation, TranslocoService } from '@ngneat/transloco';
import { get } from 'lodash-es';

import { translateColumnHeaders } from '@grid';

import { MenuItem } from 'primeng/api';

import { CONSTANTS } from '@core/constants';
import { Memoize } from '@core/decorators';
import { DetailsMenuOptions, DialogDimensions } from '@core/interfaces';
import { ConfirmationService, EnvironmentService, EventService, NotificationService, WindowService } from '@core/services';
import { formatDateTimeLong } from '@util/form';
import { remToPx } from '@util/layout';

import { endpoints } from '@environment';

import {
  ColumnFormatter,
  Columns,
  GridEvent,
  GridHeight,
  GridListRequest,
  GridLoadRequest,
  GridLoadResponseBody,
  GridRemoveRequest,
  GridRemoveResponseBody,
  GridSaveConflictCheckRequest,
  GridSaveConflictCheckResponseBody,
  GridSaveRequest,
  GridSaveResponseBody,
} from '#types/grid';
import { ListOfValuesResponse } from '#types/list';

import * as GridActions from './grid.actions';
import * as fromGrid from './grid.selectors';

@Injectable({
  providedIn: 'root',
})
export class GridService {
  sidebar$ = this.store.select(fromGrid.selectSidebar);

  filtersPanel$ = this.store.select(fromGrid.selectFilters);
  columnSettingsPanel$ = this.store.select(fromGrid.selectColumnSettings);
  exportDataPanel$ = this.store.select(fromGrid.selectExportData);
  saveGridPanel$ = this.store.select(fromGrid.selectSaveGrid);
  loadGridPanel$ = this.store.select(fromGrid.selectLoadGrid);

  columnsDialogDimensions$ = new BehaviorSubject<DialogDimensions>({
    container: { width: 0, height: 0 },
    element: { width: 0, height: 0 },
  });

  gridSaveConflictCheckResult$ = new Subject<GridSaveConflictCheckResponseBody>();

  height$ = new BehaviorSubject<GridEvent<string>>(null);

  gridRefresh$ = new Subject<string>();
  gridReset$ = new Subject<string>();
  gridLoad$ = new Subject<string>();

  editSelectedRow$ = new Subject<string>();

  rowHeightRem: number = 1.75;
  rowHeightPx: number = remToPx(this.rowHeightRem);

  gridIds$ = this.store.select(fromGrid.selectGridIds);

  savedGridList$ = (id: string) => this.store.select(fromGrid.selectSavedGridList(id));

  grid$ = <T>(id: string) => this.store.select(fromGrid.selectGrid<T>(id));
  initialized$ = (id: string) => this.store.select(fromGrid.selectGridInitialized(id));

  filters$ = (id: string) => this.store.select(fromGrid.selectGridFilters(id));
  sortsObject$ = (id: string) => this.store.select(fromGrid.selectGridSortsObject(id));
  sorts$ = (id: string) => this.store.select(fromGrid.selectGridSorts(id));

  data$ = <T>(id: string) => this.store.select(fromGrid.selectGridData<T>(id));
  selectedRow$ = <T>(id: string) => this.store.select(fromGrid.selectGridSelectedRow<T>(id));
  selectedRows$ = <T = Record<string, any>>(id: string) => this.store.select(fromGrid.selectGridSelectedRows<T>(id));

  summaryRow$ = (id: string) => this.store.select(fromGrid.selectGridSummaryRow(id));
  summaryRowText$ = (id: string) => this.store.select(fromGrid.selectGridSummaryRowText(id));
  availableColumns$ = (id: string) => this.store.select(fromGrid.selectGridAvailableColumns(id));
  selectedColumns$ = (id: string) => this.store.select(fromGrid.selectGridSelectedColumns(id));

  defaultFilters$ = (id: string) => this.store.select(fromGrid.selectGridDefaultFilters(id));
  defaultSorts$ = (id: string) => this.store.select(fromGrid.selectGridDefaultSorts(id));
  defaultAvailableColumns$ = (id: string) => this.store.select(fromGrid.selectGridDefaultAvailableColumns(id));
  defaultSelectedColumns$ = (id: string) => this.store.select(fromGrid.selectGridDefaultSelectedColumns(id));

  first$ = (id: string) => this.store.select(fromGrid.selectGridFirst(id));
  page$ = (id: string) => this.store.select(fromGrid.selectGridPage(id));
  rows$ = (id: string) => this.store.select(fromGrid.selectGridRows(id));
  rowsDisplayed$ = (id: string) => this.store.select(fromGrid.selectGridRowsDisplayed(id));
  totalRows$ = (id: string) => this.store.select(fromGrid.selectGridTotalRows(id));

  saveId$ = (id: string) => this.store.select(fromGrid.selectGridSaveId(id));
  saveName$ = (id: string) => this.store.select(fromGrid.selectGridSaveName(id));

  rowEditSave$ = new Subject<{ index: number; data: any }>();

  translations: Translation = {};
  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);
  language: string;

  sidebarExpanded: boolean;

  constructor(
    private store: Store,
    private translocoService: TranslocoService,
    private windowService: WindowService,
    private confirmationService: ConfirmationService,
    private notificationService: NotificationService,
    private environmentService: EnvironmentService,
    private http: HttpClient,
    private eventService: EventService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe((translations) => {
        this.translations = translations;
      });

    this.translocoService.langChanges$.pipe(distinctUntilChanged()).subscribe((language) => {
      this.language = language;
    });

    this.sidebar$.pipe().subscribe((state) => {
      this.sidebarExpanded = state === 'expanded';
    });

    this.windowService.viewportWidth$.pipe().subscribe((width) => {
      if (this.sidebarExpanded && width <= 576) {
        this.toggleSidebar();
      }

      if (!this.sidebarExpanded && width > 576) {
        this.toggleSidebar();
      }
    });
  }

  editSelectedRow(id: string) {
    this.editSelectedRow$.next(id);
  }

  listGrids(payload: GridListRequest) {
    return this.http.post(endpoints.grid.list, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.LIST_SAVED_VIEWS),
      map((response: ListOfValuesResponse) => response)
    );
  }

  loadGrid(payload: GridLoadRequest) {
    return this.http.post(endpoints.grid.load, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_LOAD_VIEW),
      map((response: GridLoadResponseBody) => response)
    );
  }

  saveGrid(payload: GridSaveRequest) {
    return this.http.post(endpoints.grid.save, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_SAVE_VIEW),
      map((response: GridSaveResponseBody) => response)
    );
  }

  saveGridConflictCheck(payload: GridSaveConflictCheckRequest) {
    return this.http.post(endpoints.grid.saveConflictCheck, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_SAVE_CONFLICT_CHECK),
      map((response: GridSaveConflictCheckResponseBody) => response)
    );
  }

  removeGrid(payload: GridRemoveRequest) {
    return this.http.post(endpoints.grid.remove, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_REMOVE_VIEW),
      map((response: GridRemoveResponseBody) => response)
    );
  }

  refreshAllGrids() {
    this.gridIds$.pipe(take(1)).subscribe((grids) => {
      if (grids.length === 0) {
        return;
      }

      for (const gridId of grids) {
        this.gridRefresh$.next(gridId);
      }
    });
  }

  refreshGridList(list: string[]) {
    if (list.length === 0) {
      return;
    }

    for (const gridId of list) {
      this.gridRefresh$.next(gridId);
    }
  }

  gridHeightRem(rows: number, height: GridHeight) {
    let gridHeight = (rows + 2) * this.rowHeightRem;

    if (gridHeight > height.max) {
      gridHeight = height.max;
    }

    if (gridHeight < height.min) {
      gridHeight = height.min;
    }

    return `${gridHeight}rem`;
  }

  toggleSidebar() {
    this.sidebar$.pipe(take(1)).subscribe((state) => {
      if (state === 'expanded') {
        this.store.dispatch(GridActions.closeSidebar());
      } else {
        this.store.dispatch(GridActions.openSidebar());
      }
    });
  }

  updateColumnHeaders(gridId: string, passThruLabels?: Record<string, string>) {
    this.selectedColumns$(gridId)
      .pipe(withLatestFrom(this.availableColumns$(gridId)), take(1))
      .subscribe(([selectedColumns, availableColumns]) => {
        selectedColumns = translateColumnHeaders(selectedColumns, this.translations, passThruLabels);
        availableColumns = translateColumnHeaders(availableColumns, this.translations, passThruLabels);

        this.store.dispatch(GridActions.SetColumns({ id: gridId, selectedColumns, availableColumns }));
      });
  }

  @Memoize()
  format(
    type: string,
    value: string | boolean | number,
    formatter: ColumnFormatter,
    language: string,
    translations: Translation
  ): string | boolean | number {
    const translate = (key: string): string => {
      return get(translations, key, key);
    };

    const empty = (val: string | number) => {
      if (val === undefined || val === null || (typeof val === 'object' && Object.entries(val).length === 0)) {
        return '';
      }

      return val;
    };

    const formatYesNo = (val: string | boolean | number) => {
      if (typeof val === 'boolean') {
        if (val) {
          return translate('core.common.yes');
        } else {
          return translate('core.common.no');
        }
      } else if (typeof val === 'number') {
        if (val >= 1) {
          return translate('core.common.yes');
        } else {
          return translate('core.common.no');
        }
      } else {
        if (val.toLowerCase() === 'y' || val.toLowerCase() === 'yes') {
          return translate('core.common.yes');
        } else if (val.toLowerCase() === 'n' || val.toLowerCase() === 'no') {
          return translate('core.common.no');
        } else {
          return val;
        }
      }
    };

    const currencyFormatter = new Intl.NumberFormat(`${language}-US`, {
      currency: 'USD',
      style: 'currency',
    });

    const money = (val: number) => {
      if (typeof val === 'number') {
        return currencyFormatter.format(val);
      }

      return '0';
    };

    const numberFormatter = new Intl.NumberFormat(`${language}-US`);

    const number = (val: number) => {
      if (typeof val === 'number') {
        return numberFormatter.format(val);
      }

      return '0';
    };

    const decimal = (val: number) => {
      if (typeof val === 'number') {
        return (Math.round(val * 100) / 100)
          .toFixed(2)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      }

      return '0';
    };

    if (type === 'string' && typeof value === 'string') {
      if (formatter === 'yn') {
        return formatYesNo(value);
      }

      if (formatter === 'empty') {
        return empty(value);
      }
    } else if (typeof value === 'number') {
      if (formatter === 'decimal') {
        return decimal(value);
      }

      if (formatter === 'money') {
        return money(value);
      }

      if (formatter === 'number') {
        return number(value);
      }
    } else if (typeof value === 'boolean') {
      if (formatter === 'yn') {
        return formatYesNo(value);
      }
    } else {
      return value;
    }

    return value;
  }

  copyRowToClipboard(columns: Columns, rows: Record<string, string | number | Date>[], includeHeaders: boolean = true): void {
    if (!columns || !rows) {
      return;
    }

    const output: string[] = [];

    if (includeHeaders) {
      const header: string[] = [];

      for (const column of columns) {
        header.push((column.header ?? '').replace(/(\\t|\\n)/g, ' '));
      }

      output.push(header.join('\t'));
    }

    for (const row of Object.values(rows)) {
      if (!row) {
        continue;
      }

      const lines: string[] = [];

      for (const column of columns) {
        let data: string = '';

        const value = row[column.field];

        if (typeof value === 'string') {
          data = value;
        } else if (typeof value === 'number') {
          data = `${value}`;
        } else if (value instanceof Date) {
          data = formatDateTimeLong(value);
        }

        lines.push((data ?? '').replace(/\\t/g, ' '));
      }

      output.push(lines.join('\t'));
    }

    navigator.clipboard
      .writeText(output.join('\n'))
      .then(() => {
        this.notificationService.info({
          title: this.translate('core.components.grid.copy_to_clipboard.title'),
          message: this.translate('core.components.grid.copy_to_clipboard.message'),
          duration: 3000,
        });
      })
      .catch((err) => {
        this.notificationService.error({
          message: err,
        });
      });
  }

  getFilterMenuItem(): MenuItem[] {
    return [
      {
        label: this.translate('core.components.grid.sidebar.filters.label'),
        tooltipOptions: {
          tooltipLabel:
            `<b>` +
            this.translate('core.components.grid.sidebar.filters.label') +
            `</b><br />` +
            this.translate('core.components.grid.sidebar.filters.title'),
          escape: false,
          showDelay: 250,
        },
        icon: 'fad fa-sliders-h',
        command: () => {
          this.store.dispatch(GridActions.openFilterPanel());
        },
      },
    ];
  }

  getRefreshMenuItem(gridId: string): MenuItem[] {
    return [
      {
        label: this.translate('core.components.grid.sidebar.grid_refresh.label'),
        tooltipOptions: {
          tooltipLabel:
            `<b>` +
            this.translate('core.components.grid.sidebar.grid_refresh.label') +
            `</b><br />` +
            this.translate('core.components.grid.sidebar.grid_refresh.title'),
          escape: false,
          showDelay: 250,
        },
        icon: 'fad fa-arrows-rotate',
        command: () => {
          this.gridRefresh$.next(gridId);
        },
      },
    ];
  }

  getColumnsMenuItem(): MenuItem[] {
    return [
      {
        label: this.translate('core.components.grid.sidebar.columns.label'),
        tooltipOptions: {
          tooltipLabel:
            `<b>` +
            this.translate('core.components.grid.sidebar.columns.label') +
            `</b><br />` +
            this.translate('core.components.grid.sidebar.columns.title'),
          escape: false,
          showDelay: 250,
        },
        icon: 'fad fa-columns',
        command: () => {
          this.store.dispatch(GridActions.openColumnPanel());
        },
      },
    ];
  }

  getExportMenuItem(): MenuItem[] {
    return [
      {
        label: this.translate('core.components.grid.sidebar.export.label'),
        tooltipOptions: {
          tooltipLabel:
            `<b>` +
            this.translate('core.components.grid.sidebar.export.label') +
            `</b><br />` +
            this.translate('core.components.grid.sidebar.export.title'),
          escape: false,
          showDelay: 250,
        },
        icon: 'fad fa-file-export',
        command: () => {
          this.store.dispatch(GridActions.openExportPanel());
        },
      },
    ];
  }

  getResetMenuItem(gridId: string): MenuItem[] {
    return [
      {
        label: this.translate('core.components.grid.sidebar.grid_reset.label'),
        tooltipOptions: {
          tooltipLabel:
            `<b>` +
            this.translate('core.components.grid.sidebar.grid_reset.label') +
            `</b><br />` +
            this.translate('core.components.grid.sidebar.grid_reset.title'),
          escape: false,
          showDelay: 250,
        },
        icon: 'fad fa-trash-can',
        command: () => {
          this.confirmationService.confirm({
            title: this.translate('core.components.grid.actions.confirm_reset.header'),
            message: this.translate('core.components.grid.actions.confirm_reset.message'),
            icon: 'fad fa-triangle-exclamation',
            acceptLabel: this.translate('core.components.grid.actions.confirm_reset.accept_button'),
            rejectLabel: this.translate('core.components.grid.actions.confirm_reset.reject_button'),
            defaultFocus: 'reject',
            accept: () => {
              this.store.dispatch(
                GridActions.Reset({
                  id: gridId,
                })
              );

              this.gridReset$.next(gridId);
            },
            acceptButtonStyleClass: 'p-button-warning',
            rejectButtonStyleClass: 'p-button-secondary',
          });
        },
      },
    ];
  }

  getCopyRowMenuItem(options: DetailsMenuOptions): MenuItem[] {
    return [
      {
        label: this.translate('core.components.grid.sidebar.copy_row.label'),
        tooltipOptions: {
          tooltipLabel:
            `<b>` +
            this.translate('core.components.grid.sidebar.copy_row.label') +
            `</b><br />` +
            this.translate('core.components.grid.sidebar.copy_row.title'),
          escape: false,
          showDelay: 250,
        },
        icon: 'fad fa-clipboard',
        disabled: options.disabled ?? false,
        command: () => {
          zip(this.selectedColumns$(options.gridId), this.selectedRows$(options.gridId))
            .pipe(take(1))
            .subscribe(([columns, rows]) => {
              this.copyRowToClipboard(columns, rows);
            });
        },
      },
    ];
  }

  getGoBackMenuItem(command: () => void): MenuItem[] {
    return [
      {
        label: this.translate('core.components.grid.sidebar.go_back.label'),
        tooltipOptions: {
          tooltipLabel:
            `<b>` +
            this.translate('core.components.grid.sidebar.go_back.label') +
            `</b><br />` +
            this.translate('core.components.grid.sidebar.go_back.title'),
          escape: false,
          showDelay: 250,
        },
        icon: 'fad fa-arrow-left',
        command,
      },
    ];
  }

  getSaveGridMenuItem(): MenuItem[] {
    return [
      {
        label: this.translate('core.components.grid.sidebar.save.label'),
        tooltipOptions: {
          tooltipLabel:
            `<b>` +
            this.translate('core.components.grid.sidebar.save.label') +
            `</b><br />` +
            this.translate('core.components.grid.sidebar.save.title'),
          escape: false,
          showDelay: 250,
        },
        icon: 'fad fa-floppy-disk',
        command: () => {
          this.store.dispatch(GridActions.openSaveGridPanel());
        },
      },
    ];
  }

  getLoadGridMenuItem(): MenuItem[] {
    return [
      {
        label: this.translate('core.components.grid.sidebar.load.label'),
        tooltipOptions: {
          tooltipLabel:
            `<b>` +
            this.translate('core.components.grid.sidebar.load.label') +
            `</b><br />` +
            this.translate('core.components.grid.sidebar.load.title'),
          escape: false,
          showDelay: 250,
        },
        icon: 'fad fa-folder-open',
        command: () => {
          this.store.dispatch(GridActions.openLoadGridPanel());
        },
      },
    ];
  }

  getMenuItem(options: DetailsMenuOptions): MenuItem[] {
    // allow for items that are disabled by default
    if (options.enabled !== undefined) {
      if (options.enabled) {
        options.disabled = false;
      } else {
        options.disabled = true;
      }
    }

    return [
      {
        label: options.label,
        tooltipOptions: {
          tooltipLabel: `<b>${options.label}</b><br />${options.title}`,
          escape: false,
          showDelay: 250,
        },
        icon: options.icon,
        command: options.command,
        disabled: options.disabled,
      },
    ];
  }

  getDetailsMenuItem(options: DetailsMenuOptions): MenuItem[] {
    let disabled: boolean = false;

    if (options.disabled || !options.selectedRow) {
      disabled = true;
    }

    return [
      {
        label: options.label,
        tooltipOptions: {
          tooltipLabel: `<b>${options.label}</b><br />${options.title}`,
          escape: false,
          showDelay: 250,
        },
        icon: options.icon,
        command: options.command,
        disabled,
      },
    ];
  }

  getDeveloperMenu(gridId: string): MenuItem[] {
    if (this.environmentService.environment.production) {
      return [];
    }

    const copyData = (data: any, message: string) => {
      navigator.clipboard
        .writeText(JSON.stringify(data, null, 2))
        .then(() => {
          this.notificationService.info({
            title: 'Copied!',
            message,
            progressBar: false,
          });
        })
        .catch((err) => {
          this.notificationService.error({
            message: err,
          });
        });
    };

    return [
      { separator: true },
      {
        label: 'Developer',
        icon: 'fad fa-code',
        styleClass: 'grid-context-menu-developer',
        items: [
          {
            label: 'Destroy Grid',
            icon: 'fad fa-bomb',
            command: () => {
              this.store.dispatch(GridActions.Destroy({ id: gridId }));

              setTimeout(() => {
                window.location.reload();
              }, 500);
            },
          },
          { separator: true },
          {
            label: 'Copy Displayed Columns',
            icon: 'fad fa-table-columns',
            command: () => {
              this.grid$(gridId)
                .pipe(take(1))
                .subscribe((grid) => {
                  const data: any = [...grid.selectedColumns];
                  copyData(data, 'A JSON object containing the displayed columns has been copied to the clipboard.');
                });
            },
          },
          {
            label: 'Copy Available Columns',
            icon: 'fad fa-line-columns',
            command: () => {
              this.grid$(gridId)
                .pipe(take(1))
                .subscribe((grid) => {
                  const data: any = [...grid.availableColumns];
                  copyData(data, 'A JSON object containing the available columns has been copied to the clipboard.');
                });
            },
          },
          {
            label: 'Copy All Columns',
            icon: 'fad fa-columns-3',
            command: () => {
              this.grid$(gridId)
                .pipe(take(1))
                .subscribe((grid) => {
                  const data: any = [...grid.selectedColumns, ...grid.availableColumns];
                  copyData(data, 'A JSON object containing the all columns has been copied to the clipboard.');
                });
            },
          },
          {
            label: 'Copy Selected Column Widths',
            icon: 'fad fa-text-width',
            command: () => {
              this.grid$(gridId)
                .pipe(take(1))
                .subscribe((grid) => {
                  const data: any = {};

                  for (const col of grid.selectedColumns) {
                    data[col.field] = col.width;
                  }

                  copyData(data, 'A JSON object containing the widths for each displayed column has been copied to the clipboard.');
                });
            },
          },
          {
            label: 'Copy Selected Column Fields',
            icon: 'fad fa-input-text',
            command: () => {
              this.grid$(gridId)
                .pipe(take(1))
                .subscribe((grid) => {
                  const data: any = [];

                  for (const col of grid.selectedColumns) {
                    data.push(col.field);
                  }

                  copyData(data, 'A JSON object containing the field names for each displayed column has been copied to the clipboard.');
                });
            },
          },
        ],
      },
    ];
  }

  getSeparator() {
    return [{ separator: true }];
  }
}
